import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    ActionIcon,
    Box,
    Button,
    Card,
    Group,
    Select,
    Stack,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { createVendorAlias, deleteVendorAlias, updateVendorAlias } from '../api/vendor-aliases';
import type { VendorAlias } from '../api/types';
import { useIsAdmin } from '../hooks/useIsAdmin';

interface VendorAliasesCardProps {
    // Numeric candidate id — keys the read query / cache invalidation AND the
    // create mutation (VI-473). Unique per vendor, unlike the compound id which
    // collides on `::default` across synthesized rows.
    catalogProductCandidateId: number;
    aliases: VendorAlias[];
}

// Known alias types used by the matching engine. `keyword` covers free-text
// signals; the others are scoped to specific feeds (CISA KEV vendor names,
// former / acquired company names).
const ALIAS_TYPE_OPTIONS = [
    { value: 'kev_vendor_name', label: 'KEV vendor name' },
    { value: 'former_name', label: 'Former name' },
    { value: 'acquisition', label: 'Acquisition' },
    { value: 'keyword', label: 'Keyword' },
];

/**
 * Vendor Aliases panel.
 *
 * - Lists existing aliases with a remove button per row.
 * - Inline "Add Alias" form pairs a type dropdown with a free-text value.
 *   Mirrors the structure of `CpeMappingsCard` so the two panels feel uniform.
 */
export const VendorAliasesCard = ({
    catalogProductCandidateId,
    aliases,
}: VendorAliasesCardProps): React.JSX.Element => {
    const queryClient = useQueryClient();
    // Admin-only add/edit/delete (VI-783). Enforcement is server-side + prod-only;
    // this just hides the affordances for non-admins.
    const { data: adminStatus } = useIsAdmin();
    const isAdmin = adminStatus?.is_admin ?? false;
    const [isAdding, setIsAdding] = useState(false);
    const [aliasType, setAliasType] = useState<string | null>('kev_vendor_name');
    const [aliasValue, setAliasValue] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editType, setEditType] = useState<string | null>(null);
    const [editValue, setEditValue] = useState('');

    const invalidate = useCallback(
        () =>
            queryClient.invalidateQueries({
                queryKey: ['vendor-aliases', catalogProductCandidateId],
            }),
        [queryClient, catalogProductCandidateId],
    );

    const resetForm = useCallback(() => {
        setIsAdding(false);
        setAliasType('kev_vendor_name');
        setAliasValue('');
    }, []);

    const addMutation = useMutation({
        mutationFn: (data: { alias_type: string; alias_value: string }) =>
            createVendorAlias({
                catalog_product_candidate_id: catalogProductCandidateId,
                alias_type: data.alias_type,
                alias_value: data.alias_value,
            }),
        onSuccess: async () => {
            await invalidate();
            resetForm();
            notifications.show({ color: 'green', message: 'Alias added.' });
        },
        onError: (err) => {
            notifications.show({
                color: 'red',
                title: 'Failed to add alias',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        },
    });

    const removeMutation = useMutation({
        mutationFn: (id: number) => deleteVendorAlias(id),
        onSuccess: async () => {
            await invalidate();
            notifications.show({ color: 'green', message: 'Removed.' });
        },
        onError: (err) => {
            notifications.show({
                color: 'red',
                title: 'Failed to remove alias',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        },
    });

    const handleAdd = useCallback(() => {
        const trimmed = aliasValue.trim();
        if (!aliasType || trimmed.length === 0) {
            return;
        }
        addMutation.mutate({ alias_type: aliasType, alias_value: trimmed });
    }, [addMutation, aliasType, aliasValue]);

    const editMutation = useMutation({
        mutationFn: (data: { id: number; alias_type: string; alias_value: string }) =>
            updateVendorAlias(data.id, {
                alias_type: data.alias_type,
                alias_value: data.alias_value,
            }),
        onSuccess: async () => {
            await invalidate();
            setEditingId(null);
            setEditType(null);
            setEditValue('');
            notifications.show({ color: 'green', message: 'Alias updated.' });
        },
        onError: (err) => {
            notifications.show({
                color: 'red',
                title: 'Failed to update alias',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        },
    });

    const startEdit = useCallback((a: VendorAlias) => {
        setEditingId(a.id);
        setEditType(a.alias_type);
        setEditValue(a.alias_value);
    }, []);

    const handleSaveEdit = useCallback(() => {
        const trimmed = editValue.trim();
        if (editingId === null || !editType || trimmed.length === 0) {
            return;
        }
        editMutation.mutate({ id: editingId, alias_type: editType, alias_value: trimmed });
    }, [editMutation, editingId, editType, editValue]);

    return (
        <Card withBorder radius="md" padding="lg" data-testid="VendorAliasesCard">
            <Group justify="space-between" mb="md">
                <Title order={4}>Vendor Aliases</Title>
                {isAdmin && !isAdding && (
                    <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconPlus size={14} />}
                        onClick={() => setIsAdding(true)}
                        data-testid="AddVendorAliasButton"
                    >
                        Add Alias
                    </Button>
                )}
            </Group>

            <Stack gap="xs">
                {aliases.length === 0 && !isAdding ? (
                    <Text c="dimmed" size="sm" data-testid="VendorAliasesEmpty">
                        No aliases configured for this vendor.
                    </Text>
                ) : (
                    aliases.map((a) =>
                        editingId === a.id ? (
                            <Box
                                key={a.id}
                                p="md"
                                style={{
                                    border: '1px solid var(--mantine-color-default-border)',
                                    borderRadius: 'var(--mantine-radius-md)',
                                }}
                                data-testid="EditVendorAliasForm"
                            >
                                <Stack gap="md">
                                    <Select
                                        label="Alias Type"
                                        data={ALIAS_TYPE_OPTIONS}
                                        value={editType}
                                        onChange={setEditType}
                                        allowDeselect={false}
                                    />
                                    <TextInput
                                        label="Alias Value"
                                        value={editValue}
                                        onChange={(e) => setEditValue(e.currentTarget.value)}
                                    />
                                    <Group justify="flex-end" gap="sm">
                                        <Button
                                            variant="subtle"
                                            onClick={() => {
                                                setEditingId(null);
                                                setEditType(null);
                                                setEditValue('');
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={handleSaveEdit}
                                            loading={editMutation.isPending}
                                            disabled={!editType || editValue.trim().length === 0}
                                        >
                                            Save
                                        </Button>
                                    </Group>
                                </Stack>
                            </Box>
                        ) : (
                            <Group key={a.id} justify="space-between" data-testid="VendorAliasRow">
                                <Group gap="xs">
                                    <Text size="sm" fw={500}>
                                        {a.alias_value}
                                    </Text>
                                    <Text size="sm" c="dimmed">
                                        {labelForAliasType(a.alias_type)}
                                    </Text>
                                </Group>
                                {isAdmin ? (
                                    <Group gap={4}>
                                        <ActionIcon
                                            variant="subtle"
                                            aria-label="Edit alias"
                                            onClick={() => startEdit(a)}
                                            data-testid="EditVendorAliasButton"
                                        >
                                            <IconPencil size={16} />
                                        </ActionIcon>
                                        <ActionIcon
                                            color="red"
                                            variant="subtle"
                                            aria-label="Remove alias"
                                            onClick={() => removeMutation.mutate(a.id)}
                                            disabled={removeMutation.isPending}
                                        >
                                            <IconTrash size={16} />
                                        </ActionIcon>
                                    </Group>
                                ) : null}
                            </Group>
                        ),
                    )
                )}
            </Stack>

            {isAdding && (
                <Box
                    mt="md"
                    p="md"
                    style={{
                        border: '1px solid var(--mantine-color-default-border)',
                        borderRadius: 'var(--mantine-radius-md)',
                    }}
                    data-testid="AddVendorAliasForm"
                >
                    <Stack gap="md">
                        <Select
                            label="Alias Type"
                            description="Which feed or signal the alias should match against."
                            data={ALIAS_TYPE_OPTIONS}
                            value={aliasType}
                            onChange={setAliasType}
                            allowDeselect={false}
                        />
                        <TextInput
                            label="Alias Value"
                            description="The exact alternate name as it appears in the source (e.g. 'Microsoft' for a KEV vendor name)."
                            value={aliasValue}
                            onChange={(e) => setAliasValue(e.currentTarget.value)}
                            placeholder="e.g. Microsoft"
                        />
                        <Group justify="flex-end" gap="sm">
                            <Button variant="subtle" onClick={resetForm}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleAdd}
                                loading={addMutation.isPending}
                                disabled={!aliasType || aliasValue.trim().length === 0}
                            >
                                Save
                            </Button>
                        </Group>
                    </Stack>
                </Box>
            )}
        </Card>
    );
};

function labelForAliasType(type: string): string {
    return ALIAS_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;
}
